import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shell/PageHeader";
import { CommandHero } from "@/components/shell/CommandHero";
import { Button } from "@/components/ui/button";
import { AgentAtWork } from "@/components/content/AgentAtWork";
import { EditablePostCard } from "@/components/content/EditablePostCard";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";
import { ReviseAgentPanel } from "@/components/content/ReviseAgentPanel";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { useAppState } from "@/state/AppStateProvider";
import { useAuth } from "@/state/AuthProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Play, StopCircle, Pause, RotateCw, CheckCircle2, Pencil, Eye, ChevronDown, ChevronRight, FlaskConical, ImageIcon as ImageLucide, FileText as FileTextIcon, RefreshCcw, Activity, Layers3, Send, ShieldCheck, AlertTriangle, ClipboardList, Search, ListChecks } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ApprovalActions } from "@/components/content/ApprovalActions";
import { cn } from "@/lib/utils";
import { attachRunImagePrompt, getRunCoverPrompt, getRunImagePrompt } from "@/lib/imagePromptAssets";
import { shouldAttachCoverToAsset } from "@/lib/mediaAspect";
import { getRunStepDisplay, runStepStatusNote } from "@/lib/runStepDisplay";
import { resolveRunForRoute } from "@/lib/runRoute";
import { formatDurationMs, formatReportList, formatResearchDecisionRows, formatSourceDigestRows, getExecutionReport } from "@/lib/executionReport";
import { formatResearchMode } from "@/lib/researchModes";

export default function AgentRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, startMockRun, advanceRun, pauseRun, stopRun, updateAsset, approveAsset, rejectAsset, reviseAsset, generateImage, uploadImage } = useAppState();
  const { user } = useAuth();
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") === "report" ? "report" : "posts");
  const [coverBusy, setCoverBusy] = useState(false);
  const coverUploadRef = useRef(null);

  const run = useMemo(() => resolveRunForRoute(state.runs, id), [state.runs, id]);
  const assets = useMemo(() => {
    if (!run) return [];
    const runAssets = run.asset_ids?.length
      ? state.assets.filter((a) => run.asset_ids.includes(a.id))
      : state.assets.filter((a) => a.project_id === run.project_id);
    return attachRunImagePrompt(runAssets);
  }, [state.assets, run]);

  const deliverables = useMemo(
    () => assets.filter((a) => !["research_brief", "image_prompt", "execution_report"].includes(a.asset_type)),
    [assets],
  );
  const selectedAsset = useMemo(
    () => deliverables.find((d) => d.id === selectedId) || deliverables[0] || null,
    [deliverables, selectedId],
  );
  const supportingWork = useMemo(
    () => assets.filter((a) => ["research_brief", "image_prompt"].includes(a.asset_type)),
    [assets],
  );
  const referenceWork = useMemo(
    () => assets.filter((a) => ["research_brief", "image_prompt", "execution_report"].includes(a.asset_type)),
    [assets],
  );
  const sharedCover = useMemo(
    () => supportingWork.find((a) => a.asset_type === "image_prompt")?.cover_image
      || deliverables.find((a) => a.cover_image)?.cover_image
      || null,
    [supportingWork, deliverables],
  );
  const coverTargets = useMemo(
    () => assets.filter((asset) => asset.asset_type === "image_prompt" || shouldAttachCoverToAsset(asset)),
    [assets],
  );
  const runProgress = useMemo(() => {
    const steps = run?.steps || [];
    const done = steps.filter((step) => ["done", "warning", "skipped"].includes(step.status)).length;
    const total = steps.length;
    const active = steps.find((step) => step.status === "running");
    return {
      done,
      total,
      pct: total ? Math.round((done / total) * 100) : 0,
      active,
    };
  }, [run]);
  const [supportingOpen, setSupportingOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("tab") === "report") setTab("report");
  }, [searchParams]);

  // Auto-advance a running run for the live-feel demo
  useEffect(() => {
    if (!run || run.status !== "running") return;
    const t = setInterval(() => advanceRun(run.id), 1800);
    return () => clearInterval(t);
  }, [run, advanceRun]);

  void user;

  const replaceSharedCover = (coverImage) => {
    coverTargets.forEach((asset) => updateAsset(asset.id, { cover_image: coverImage }));
  };

  const onRegenerateCover = async () => {
    setCoverBusy(true);
    const pending = toast.loading("Generating cover…");
    try {
      const prompt = getRunCoverPrompt(assets)
        || `A polished, brand-safe social media cover image for "${run.title}". No text, logos, or watermarks.`;
      const res = await generateImage(prompt, undefined, "wide");
      if (res?.ok) { replaceSharedCover(res.url); toast.success("Cover regenerated", { id: pending }); }
      else toast.error(`Image generation failed: ${res?.message || "unknown error"}`, { id: pending });
    } finally {
      setCoverBusy(false);
    }
  };

  const onUploadCover = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    if (file.size > 12 * 1024 * 1024) return toast.error("Image too large - keep it under 12MB");
    const reader = new FileReader();
    reader.onload = async () => {
      setCoverBusy(true);
      const pending = toast.loading("Uploading cover…");
      try {
        const res = await uploadImage(reader.result);
        if (res?.ok) { replaceSharedCover(res.url); toast.success(`${file.name} applied`, { id: pending }); }
        else toast.error(`Upload failed: ${res?.message || "unknown error"}`, { id: pending });
      } finally {
        setCoverBusy(false);
      }
    };
    reader.onerror = () => toast.error("Could not load image");
    reader.readAsDataURL(file);
  };

  const onRerun = () => {
    const nextId = startMockRun({
      project_id: run.project_id,
      title: run.title,
      model: run.model,
      model_provider: run.model_provider,
      model_label: run.model_label,
      model_name: run.model_name,
      model_configured: run.model_configured,
      platforms: run.platforms,
      skills_used: run.skills_used,
      audience: run.audience,
      goal: run.goal,
      research_mode: run.research_mode,
      image_mode: run.image_mode,
      uploaded_image: run.uploaded_image,
    });
    toast.success("Run restarted");
    navigate(`/runs/${nextId}`);
  };

  if (!run) {
    return (
      <div className="space-y-4" data-testid="agent-run-page">
        <PageHeader
          title="Agent run"
          eyebrow="Run"
          description={id ? `No run found for ${id}.` : "No runs found yet."}
        />
        <Button asChild><Link to="/create">Start a new run</Link></Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-7" data-testid="agent-run-page">
      <CommandHero
        dark
        eyebrow={`Run ${run.id}`}
        title={run.title}
        testId="agent-run-command-hero"
        visual={<AgentRunHeroVisual run={run} progress={runProgress} deliverables={deliverables} supportingWork={referenceWork} />}
        description={`For ${run.audience} - ${formatResearchMode(run.research_mode)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {run.status === "running" ? (
              <>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { pauseRun(run.id); toast.message("Run paused"); }} data-testid="agent-run-pause"><Pause className="h-3.5 w-3.5" /> Pause</Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { stopRun(run.id); toast.error("Run stopped"); }} data-testid="agent-run-stop-button"><StopCircle className="h-3.5 w-3.5" /> Stop</Button>
              </>
            ) : (
              <Button size="sm" className="gap-1.5" onClick={onRerun} data-testid="agent-run-rerun" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}><RotateCw className="h-3.5 w-3.5" /> Re-run</Button>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { assets.forEach((a) => approveAsset(a.id)); toast.success("Batch approved"); }} data-testid="agent-run-approve-batch"><CheckCircle2 className="h-3.5 w-3.5" /> Approve all</Button>
          </div>
        }
      />

      {/* MISSION CONTROL - cinematic agent working visualization */}
      <AgentAtWork
        run={run}
        progress={runProgress}
        deliverableCount={deliverables.length}
        supportingCount={referenceWork.length}
        assets={assets}
        deliverables={deliverables}
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
            onClick={() => setTab("report")}
          >
            <ClipboardList className="h-3.5 w-3.5" /> Execution Report
          </Button>
        }
      />

      {/* OUTPUT - post-first */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList className="bg-transparent p-0 gap-1" data-testid="agent-run-tabs">
            <TabsTrigger value="posts" className="" data-testid="agent-run-tab-posts">
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Generated posts ({deliverables.length})
            </TabsTrigger>
            <TabsTrigger value="report" className="" data-testid="agent-run-tab-report">
              <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Execution report
            </TabsTrigger>
          </TabsList>
          {deliverables.length > 0 && (
            <p className="text-[11.5px] text-muted-foreground">Click <span className="text-1">Edit</span> on any post to fine-tune before approving.</p>
          )}
        </div>

        <TabsContent value="posts" className="mt-5 space-y-6">
          {deliverables.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-12 text-center" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-sm text-1">{run.status === "running" ? "Drafts will appear here as the agent finishes generation..." : "No drafts yet."}</p>
              <p className="mt-1 text-xs text-muted-foreground">{run.status === "running" ? "Watch the phase tiles above - the post lands the moment 'Generated drafts' completes." : "Re-run to produce a new package."}</p>
            </div>
          ) : (
            <>
              {/* Two-column review: draft "proof" cards on the left, the docked
                  revise agent on the right acting on the selected card (#22). */}
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start">
                <div className="space-y-5">
                  {deliverables.map((a) => {
                    const isSelected = selectedAsset?.id === a.id;
                    return (
                      <div
                        key={a.id}
                        onClick={() => setSelectedId(a.id)}
                        data-testid={`agent-run-asset-${a.id}`}
                        className={cn(
                          "cursor-pointer space-y-2 rounded-2xl border-2 p-2 transition-[border-color,box-shadow] duration-200",
                          isSelected
                            ? "border-[color:var(--accent-blue)] shadow-[0_0_26px_-6px_rgba(18,147,79,0.35)]"
                            : "border-transparent hover:border-[color:var(--border-subtle)]",
                        )}
                      >
                        <div className="flex items-center justify-between px-1">
                          <div className="flex items-center gap-2">
                            <PlatformBadge platform={a.platform} compact />
                            <span className="text-[11.5px] text-muted-foreground">{a.title}</span>
                          </div>
                          <StatusBadge status={a.status} />
                        </div>
                        <PlatformPreviewPanel asset={a} compact className="shadow-none" bodyClassName="bg-[color:var(--surface-2)]" />
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-2.5" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} onClick={(e) => e.stopPropagation()}>
                          <ApprovalActions
                            showRegenerate={false}
                            onApprove={() => { approveAsset(a.id); toast.success("Approved"); }}
                            onRevise={() => setSelectedId(a.id)}
                            onReject={() => { rejectAsset(a.id); toast.error("Rejected"); }}
                          />
                          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditingAsset(a)} data-testid={`agent-run-edit-${a.id}`}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <aside className="xl:sticky xl:top-20 xl:self-start">
                  {selectedAsset ? (
                    <ReviseAgentPanel
                      asset={selectedAsset}
                      onRevise={(instruction) => reviseAsset(selectedAsset.id, { instruction, mode: "revise" })}
                      onRegenerate={(instruction) => reviseAsset(selectedAsset.id, { instruction: instruction || "Fresh take — a new angle on the same idea", mode: "regenerate" })}
                      onApprove={() => { approveAsset(selectedAsset.id); toast.success("Approved"); }}
                    />
                  ) : null}
                </aside>
              </div>

              {/* Supporting work - collapsed by default */}
              {supportingWork.length > 0 && (
                <section className="rounded-2xl border bg-card inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="agent-run-supporting">
                  <button
                    type="button"
                    onClick={() => setSupportingOpen((v) => !v)}
                    className="flex w-full items-center justify-between gap-2 px-5 py-3.5 text-left hover:bg-white/[0.02]"
                    data-testid="agent-run-supporting-toggle"
                  >
                    <div className="flex items-center gap-2.5">
                      {supportingOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <FlaskConical className="h-4 w-4 text-[color:var(--accent-cyan)]" />
                      <div>
                        <p className="text-sm font-semibold text-1">Supporting materials</p>
                        <p className="text-[11.5px] text-muted-foreground">Cover image, image prompt, and research notes created during this run.</p>
                      </div>
                    </div>
                    <span className="text-[11px] text-muted-foreground">{supportingWork.length} item{supportingWork.length === 1 ? "" : "s"}</span>
                  </button>
                  {supportingOpen && (
                    <div className="border-t p-5" style={{ borderColor: "var(--border-subtle)" }}>
                      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                        {/* Shared cover image - applied to every visual platform */}
                        {sharedCover && (
                          <div className="rounded-xl border bg-[color:var(--surface-1)] p-4" style={{ borderColor: "var(--border-subtle)" }} data-testid="agent-run-shared-cover">
                            <div className="mb-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <ImageLucide className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                                <h4 className="text-[13px] font-semibold text-1">Shared cover image</h4>
                              </div>
                              <span className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">Applied to all visual platforms</span>
                            </div>
                            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
                              <img src={sharedCover} alt="Cover" className="block w-full" />
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <Button size="sm" variant="outline" className="gap-1.5" onClick={onRegenerateCover} disabled={coverBusy} data-testid="agent-run-regen-cover">
                                <RefreshCcw className={cn("h-3.5 w-3.5", coverBusy && "animate-spin")} /> Regenerate
                              </Button>
                              <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => coverUploadRef.current?.click()} disabled={coverBusy} data-testid="agent-run-upload-cover">
                                <ImageLucide className="h-3.5 w-3.5" /> Upload your own
                              </Button>
                              <input ref={coverUploadRef} type="file" accept="image/*" className="hidden" onChange={onUploadCover} data-testid="agent-run-cover-input" />
                              <span className="ml-auto text-[10.5px] text-muted-foreground">Auto-resized per platform spec</span>
                            </div>
                          </div>
                        )}

                        {/* Image prompt text */}
                        {supportingWork.filter((a) => a.asset_type === "image_prompt").map((a) => (
                          <div key={a.id} className="rounded-xl border bg-[color:var(--surface-1)] p-4" style={{ borderColor: "var(--border-subtle)" }}>
                            <div className="mb-2 flex items-center gap-2">
                              <FileTextIcon className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                              <h4 className="text-[13px] font-semibold text-1">Image prompt</h4>
                            </div>
                            <p className="text-[12.5px] leading-[1.6] text-foreground/85 whitespace-pre-wrap">{a.body}</p>
                          </div>
                        ))}

                        {/* Research notes */}
                        {supportingWork.filter((a) => a.asset_type === "research_brief").map((a) => (
                          <div key={a.id} className="rounded-xl border bg-[color:var(--surface-1)] p-4 lg:col-span-2" style={{ borderColor: "var(--border-subtle)" }}>
                            <div className="mb-2 flex items-center gap-2">
                              <FlaskConical className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                              <h4 className="text-[13px] font-semibold text-1">Research notes</h4>
                            </div>
                            <p className="text-[12.5px] leading-[1.6] text-foreground/85 whitespace-pre-wrap">{a.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="report" className="mt-5">
          <ExecutionReport run={run} assets={assets} deliverables={deliverables} supportingWork={referenceWork} progress={runProgress} />
        </TabsContent>
      </Tabs>

      {/* Edit asset sheet */}
      <Sheet open={!!editingAsset} onOpenChange={(o) => !o && setEditingAsset(null)}>
        <SheetContent side="right" className="w-full max-w-[640px] overflow-y-auto scrollbar-thin" data-testid="agent-run-edit-sheet" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
          {editingAsset && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base font-semibold text-1">Edit - {editingAsset.title}</SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">Changes save automatically and reflect in the live preview.</SheetDescription>
              </SheetHeader>
              <div className="mt-5">
                <EditablePostCard asset={editingAsset} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </motion.div>
  );
}
function ExecutionReport({ run, assets, deliverables, supportingWork, progress }) {
  const researchBrief = supportingWork.find((asset) => asset.asset_type === "research_brief");
  const executionReport = getExecutionReport(assets);
  const breakdown = executionReport?.prompt_breakdown || {};
  const structure = executionReport?.explicit_structure || {};
  const sourceDigest = executionReport?.source_digest || null;
  const researchDecisionRows = formatResearchDecisionRows(executionReport);
  const workflowSteps = executionReport?.workflow_events?.length ? executionReport.workflow_events : (run.steps || []);
  const assetRows = assets.map((asset) => ({
    ...asset,
    displayType: assetTypeLabel(asset.asset_type),
    displayStatus: statusLabel(asset.status),
  }));

  return (
    <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]" data-testid="execution-report">
      <div className="space-y-5">
        <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <h2 className="text-base font-semibold tracking-[-0.01em] text-1">Execution report</h2>
            <p className="mt-1 text-sm text-muted-foreground">A plain-English record of what the agent was asked to do, what instructions it followed, and how it produced the posts.</p>
          </div>
        </div>

        <ReportSection icon={FileTextIcon} title="Input and configuration">
          <ReportGrid
            items={[
              ["Prompt", breakdown.raw_prompt || run.prompt || run.title],
              ["Audience", executionReport?.audience || breakdown.audience || run.audience],
              ["Platforms", (run.platforms || []).join(", ") || "None selected"],
              ["Model", executionReport?.model || formatRunModel(run)],
              ["Research mode", formatResearchMode(run.research_mode)],
              ["Assets produced", `${deliverables.length} posts, ${supportingWork.length} supporting items`],
            ]}
          />
        </ReportSection>

        <ReportSection icon={ClipboardList} title="Agent understanding">
          {executionReport ? (
            <div className="space-y-3">
              <ReportGrid
                items={[
                  ["Topic", breakdown.topic || "Not specified"],
                  ["Post type", breakdown.post_type || "Not specified"],
                  ["Prompt direction", breakdown.direction || "Not specified"],
                  ["Tone/style", structure.tone || "Not specified"],
                  ["CTA", formatCtaReport(breakdown.cta)],
                  ["Platform hints", formatReportList(breakdown.platform_hints)],
                ]}
              />
              <ReportCallout
                label="Prompt sequence"
                value={formatReportList(structure.sequence || breakdown.explicit_directions)}
              />
            </div>
          ) : (
            <EmptyReportState message="This run does not have a V2 execution report asset yet. New V2 runs will show the agent's prompt breakdown here." />
          )}
        </ReportSection>

        <ReportSection icon={ListChecks} title="Required structure">
          {executionReport ? (
            <div className="space-y-3">
              <ReportGrid
                items={[
                  ["Format", formatStructureName(structure.format)],
                  ["Item count", structure.item_count ?? "Not specified"],
                  ["Item subject", structure.item_subject || "Not specified"],
                  ["Length", structure.length || "Not specified"],
                  ["Must include", formatReportList(structure.must_include)],
                  ["Must avoid", formatReportList(structure.must_avoid)],
                ]}
              />
              <ReportCallout
                label="Raw user instruction kept as source of truth"
                value={structure.raw_instruction || breakdown.raw_prompt || run.prompt || "Not recorded"}
              />
            </div>
          ) : (
            <EmptyReportState message="No structured prompt contract was captured for this run." />
          )}
        </ReportSection>

        <ReportSection icon={Activity} title="Plan followed">
          <ol className="space-y-2">
            {workflowSteps.map((step, index) => (
              <ExecutionStepRow key={step.id || index} step={step} index={index} />
            ))}
          </ol>
        </ReportSection>

        <ReportSection icon={Layers3} title="Instructions applied">
          <div className="flex flex-wrap gap-2">
            {(run.skills_used || []).map((skill) => (
              <span key={skill} className="rounded-md border bg-[color:var(--surface-2)] px-2.5 py-1.5 font-mono text-[12px] text-1" style={{ borderColor: "var(--border-subtle)" }}>
                {skill}.md
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm text-muted-foreground">These instructions shaped the research, voice, critique pass, and platform formatting for this run.</p>
        </ReportSection>

        <ReportSection icon={Search} title="Research and source decisions">
          {executionReport && sourceDigest ? (
            <div className="space-y-3">
              <ReportGrid
                items={[
                  ...researchDecisionRows,
                  ...formatSourceDigestRows(executionReport),
                  ["Sources digested", sourceDigest.source_count ?? 0],
                  ["Approved facts", sourceDigest.approved_facts?.length || 0],
                  ["Objective claims", sourceDigest.objective_claims?.length || 0],
                  ["Warnings", sourceDigest.warnings?.length || 0],
                ]}
              />
              <ReportFactList facts={sourceDigest.approved_facts} />
              <ReportWarningList warnings={sourceDigest.warnings} />
            </div>
          ) : run.research_mode === "none" ? (
            <p className="text-sm text-muted-foreground">No web research was requested for this run. Uploaded files, pasted URLs, and source notes can still ground the draft.</p>
          ) : researchBrief?.body ? (
            <div className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Captured research notes</p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-1">{researchBrief.body}</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-sm font-medium text-1">No web research sources were captured for this run.</p>
              <p className="mt-1 text-sm text-muted-foreground">If research was enabled, the provider returned no usable sources or the run was created before provider notes were recorded.</p>
            </div>
          )}
        </ReportSection>

        <ReportSection icon={ListChecks} title="Output structure and quality checks">
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {(run.platforms || []).map((platform) => (
                <div key={platform} className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
                  <PlatformBadge platform={platform} compact />
                  <p className="mt-2 text-sm text-1">{platformStructure(platform)}</p>
                </div>
              ))}
            </div>
            <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
              <li>Checked voice against the selected brand voice and skills.</li>
              <li>Checked CTA style and removed hype or vague phrasing.</li>
              <li>Held generated posts for approval instead of publishing automatically.</li>
            </ul>
          </div>
        </ReportSection>

        {executionReport?.drafting_rules?.length > 0 && (
          <ReportSection icon={ShieldCheck} title="Drafting rules applied">
            <ul className="space-y-2 text-sm text-1">
              {executionReport.drafting_rules.map((rule) => (
                <li key={rule} className="rounded-lg border bg-[color:var(--surface-2)] px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                  {rule}
                </li>
              ))}
            </ul>
          </ReportSection>
        )}
      </div>

      <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
        <div className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
          <h3 className="text-sm font-semibold text-1">Run outcome</h3>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <ReportMetric label="Progress" value={`${progress.pct}%`} />
            <ReportMetric label="Generated" value={deliverables.length} />
            <ReportMetric label="Supporting" value={supportingWork.length} />
            <ReportMetric label="Status" value={run.status} />
          </div>
        </div>
        <div className="rounded-xl border bg-[color:var(--surface-1)] p-4" style={{ borderColor: "var(--border-subtle)" }}>
          <h3 className="text-sm font-semibold text-1">Generated assets</h3>
          <div className="mt-3 space-y-2">
            {assetRows.map((asset) => (
              <div key={asset.id} className="rounded-lg border bg-card px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                <p className="truncate text-[13px] font-medium text-1">{asset.title}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{asset.displayType} - {asset.displayStatus}</p>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </section>
  );
}

function ExecutionStepRow({ step, index }) {
  const display = getRunStepDisplay(step);
  const duration = step.duration_ms !== undefined ? formatDurationMs(step.duration_ms) : "";
  const statusClass = step.status === "failed"
    ? "bg-red-50 text-red-700 ring-red-100"
    : step.status === "warning"
      ? "bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)] ring-amber-100"
      : step.status === "done"
        ? "bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)] ring-emerald-100"
        : "bg-card text-muted-foreground ring-slate-200";
  return (
    <li className="flex items-start gap-3 rounded-lg border bg-[color:var(--surface-2)] px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--accent-blue)]/12 text-[11px] font-semibold text-[color:var(--accent-cyan)]">{index + 1}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-1">{display.label}</span>
        <span className="block text-[12px] text-muted-foreground">{display.note || runStepStatusNote(step.status)}</span>
      </span>
      {duration && (
        <span className="rounded-full bg-card px-2 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-slate-200">{duration}</span>
      )}
      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ring-1", statusClass)}>{step.status || "pending"}</span>
    </li>
  );
}

function ReportSection({ icon: Icon, title, children }) {
  return (
    <section className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md border bg-[color:var(--surface-2)]" style={{ borderColor: "var(--border-subtle)" }}>
          <Icon className="h-4 w-4 text-[color:var(--accent-cyan)]" />
        </span>
        <h3 className="text-sm font-semibold text-1">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function ReportGrid({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
          <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</dt>
          <dd className="mt-1 text-sm font-medium text-1">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ReportMetric({ label, value }) {
  return (
    <div className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
      <p className="text-lg font-bold tracking-[-0.03em] text-1">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function ReportCallout({ label, value }) {
  return (
    <div className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
      <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-1">{value || "Not specified"}</p>
    </div>
  );
}

function EmptyReportState({ message }) {
  return (
    <div className="rounded-lg border border-dashed bg-[color:var(--surface-2)] p-4" style={{ borderColor: "var(--border-subtle)" }}>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function ReportFactList({ facts = [] }) {
  if (!Array.isArray(facts) || facts.length === 0) {
    return <p className="text-sm text-muted-foreground">No approved facts were captured.</p>;
  }
  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Approved facts</p>
      {facts.slice(0, 5).map((fact, index) => (
        <div key={`${fact.source_title || "fact"}-${index}`} className="rounded-lg border bg-[color:var(--surface-2)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
          <p className="text-sm leading-6 text-1">{fact.text}</p>
          <p className="mt-2 text-[11.5px] text-muted-foreground">
            {(fact.source_title || fact.source_url || "Source not named")} - {fact.confidence || "confidence not set"}
          </p>
          {fact.qualification && <p className="mt-1 text-[11.5px] text-muted-foreground">{fact.qualification}</p>}
        </div>
      ))}
    </div>
  );
}

function ReportWarningList({ warnings = [] }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <div className="rounded-lg border bg-[color:var(--accent-amber)]/12 p-3" style={{ borderColor: "rgba(217,119,6,0.22)" }}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--accent-amber)]">Source warnings</p>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-amber-950">
        {warnings.slice(0, 5).map((warning) => <li key={warning}>{warning}</li>)}
      </ul>
    </div>
  );
}

function assetTypeLabel(assetType) {
  const labels = {
    research_brief: "Research notes",
    image_prompt: "Image prompt",
    execution_report: "Execution report",
    social_post: "Social post",
    thread: "Thread",
    video_script: "Video script",
    carousel: "Carousel",
    carousel_outline: "Carousel outline",
    blog: "Blog post",
    imported_draft: "Imported draft",
    linkedin_post: "LinkedIn post",
    x_thread: "X thread",
    youtube_short: "YouTube Short",
    instagram_carousel: "Instagram carousel",
    blog_post: "Blog post",
  };
  return labels[assetType] || assetType?.replace(/_/g, " ") || "Asset";
}

function statusLabel(status) {
  const labels = {
    needs_review: "Needs review",
    supporting: "Supporting",
    reference: "Reference",
  };
  return labels[status] || status?.replace(/_/g, " ") || "Unknown";
}

function formatCtaReport(cta = {}) {
  if (!cta || (!cta.mode && !cta.direction)) return "Not specified";
  if (cta.mode === "none") return "No CTA requested";
  return [cta.mode || "CTA", cta.direction].filter(Boolean).join(" - ");
}

function formatStructureName(value) {
  return value ? value.replace(/_/g, " ") : "Not specified";
}

function formatRunModel(run) {
  if (run.model_label && run.model_name) return `${run.model_label} / ${run.model_name}`;
  if (run.model_label) return run.model_label;
  return run.model || "Not recorded";
}

function platformStructure(platform) {
  const map = {
    linkedin: "Hook -> stance -> evidence -> takeaway -> soft CTA.",
    x: "Threaded sequence with standalone posts and no clickbait.",
    youtube_shorts: "Cold open -> three beats -> short CTA.",
    instagram: "Carousel outline with one idea per slide.",
    threads: "Conversational post with a clear opinion and reply-friendly close.",
    facebook: "Community-oriented post with plain-language context.",
    bluesky: "Short post with one clear idea and minimal formatting.",
    blog: "Long-form markdown with sections, examples, and a grounded close.",
  };
  return map[platform] || "Platform formatter applied before approval.";
}

function AgentRunHeroVisual({ run, progress, deliverables, supportingWork }) {
  const isDone = run.status === "completed";
  const isRunning = run.status === "running";
  const isFailed = run.status === "failed";
  const statusLabel = isDone ? "Ready for review" : isRunning ? "Running now" : isFailed ? "Needs attention" : "Queued";
  const StatusIcon = isFailed ? AlertTriangle : isDone ? CheckCircle2 : Activity;

  const stats = [
    { label: "Progress", value: `${progress.pct}%`, icon: Activity, color: "text-[color:var(--accent-cyan)]", bg: "bg-[color:var(--accent-blue)]/12" },
    { label: "Platforms", value: run.platforms?.length || 0, icon: Send, color: "text-[color:var(--accent-green)]", bg: "bg-[color:var(--accent-green)]/12" },
    { label: "Deliverables", value: deliverables.length, icon: Layers3, color: "text-[color:var(--accent-green)]", bg: "bg-[color:var(--accent-green)]/12" },
    { label: "Supporting work", value: supportingWork.length, icon: FlaskConical, color: "text-amber-600", bg: "bg-[color:var(--accent-amber)]/12" },
  ];

  return (
    <div className="space-y-3" data-testid="agent-run-hero-status">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">{statusLabel}</p>
          <p className="text-[12px] text-muted-foreground">
            {progress.active ? `Currently: ${progress.active.label}` : isDone ? "All phases finished." : isFailed ? run.error || "Run stopped." : "Waiting to start."}
          </p>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium",
            isFailed ? "bg-red-50 text-red-700" : isDone ? "bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]" : "bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
          )}
          style={{ borderColor: isFailed ? "rgba(220,38,38,0.18)" : isDone ? "rgba(22,163,74,0.18)" : "rgba(18, 147, 79,0.18)" }}
        >
          <StatusIcon className="h-3 w-3" /> {run.status}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-[color:var(--surface-3)]">
        <div
          className={cn("h-full rounded-full transition-all", isFailed ? "bg-red-500" : isDone ? "bg-emerald-500" : "bg-[color:var(--accent-blue)]")}
          style={{ width: `${progress.pct}%` }}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {stats.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="rounded-md border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between gap-2">
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${bg}`}>
                <Icon className={`h-3.5 w-3.5 ${color}`} />
              </span>
              <span className="text-lg font-bold tracking-[-0.03em] text-foreground">{value}</span>
            </div>
            <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(run.platforms || []).map((platform) => <PlatformBadge key={platform} platform={platform} compact />)}
      </div>
      <div className="flex items-center gap-2 rounded-md border bg-[color:var(--surface-1)] px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
        <p className="text-[12px] font-medium text-[color:var(--text-2)]">Outputs stay approval-first.</p>
      </div>
    </div>
  );
}
